import React, { useRef, useEffect, useState } from "react";
import { Column } from "@amzn/meridian";
import CaptureButton from "../../inspection/components/CaptureButton";
import { captureVideoFrame } from "../../inspection/utils/videoUtils";
import "./SimpleCamera.scss";

interface SimpleCameraProps {
  onCapture: (file: File) => void;
  onCancel?: () => void;
  screenWidth?: number;
  screenHeight?: number;
}

/**
 * SimpleCamera - A lightweight camera component for capturing single images
 * 
 * This component:
 * - Initializes camera on mount
 * - Shows live video preview
 * - Captures a single frame when user clicks capture button
 * - Cleans up camera resources on unmount
 * 
 * Unlike CameraFrame, this is designed for simple capture workflows
 * without streaming, detection overlays, or complex inspection logic.
 */
const SimpleCamera: React.FC<SimpleCameraProps> = ({
  onCapture,
  onCancel,
  screenWidth = 1920,
  screenHeight = 1080,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);

  // Initialize camera on mount
  useEffect(() => {
    let isMounted = true;
    let playPromise: Promise<void> | undefined;

    const startCamera = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        if (isMounted) {
          setError("Camera not supported in this browser");
        }
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "environment",
            width: { ideal: screenWidth },
            height: { ideal: screenHeight },
          },
          audio: false,
        });

        if (!isMounted) {
          // Component unmounted during async operation, cleanup stream
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          
          // Handle play promise properly to avoid interruption errors
          try {
            playPromise = videoRef.current.play();
            await playPromise;
            
            if (isMounted) {
              setIsReady(true);
            }
          } catch (playError: any) {
            // Ignore AbortError which happens when play is interrupted
            if (playError.name !== 'AbortError') {
              console.error('Error playing video:', playError);
              if (isMounted) {
                setError(`Failed to start video: ${playError.message}`);
              }
            }
          }
        }
      } catch (e: any) {
        if (!isMounted) return;
        
        const name = e?.name || "";
        if (name === "NotAllowedError") {
          setError("Camera access denied. Please allow camera access.");
        } else if (name === "NotFoundError") {
          setError("No camera found on this device.");
        } else {
          setError(`Camera error: ${e?.message || String(e)}`);
        }
      }
    };

    startCamera();

    // Cleanup: Stop camera on unmount
    return () => {
      isMounted = false;
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        // Pause video before clearing srcObject to prevent play interruption
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      }
    };
  }, [screenWidth, screenHeight]);

  const handleCapture = async () => {
    if (!videoRef.current || !isReady) return;

    setIsCapturing(true);

    try {
      const capturedFile = await captureVideoFrame(videoRef.current);
      
      // Stop camera immediately after capture
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      }
      
      setIsReady(false);
      onCapture(capturedFile);
    } catch (error) {
      console.error("Failed to capture frame:", error);
      setError("Failed to capture image. Please try again.");
      setIsCapturing(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Column className="simple-camera-viewport">
      {/* Video Preview */}
      <video
        ref={videoRef}
        className="simple-camera-video"
        autoPlay
        muted
        playsInline
      />

      {/* Camera Frame Wrapper */}
      <div className="simple-camera-frame-wrapper">
        {isReady && (
          <Column className="simple-camera-frame">
            <Column className="simple-camera-top-line" />
            <Column className="simple-camera-left-line" />
            <Column className="simple-camera-right-line" />
            <Column className="simple-camera-bottom-line" />
          </Column>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="simple-camera-error">
          <p>{error}</p>
        </div>
      )}

      {/* Loading State */}
      {!isReady && !error && (
        <div className="simple-camera-loading">
          <p>Initializing camera...</p>
        </div>
      )}

      {/* Capture Button - Using shared CaptureButton component */}
      {isReady && !error && (
        <CaptureButton 
          onClick={handleCapture}
          disabled={isCapturing}
        />
      )}

      {/* Cancel Button - Top Left Corner */}
      {onCancel && (
        <div className="simple-camera-cancel-wrapper">
          <button
            className="simple-camera-cancel-button"
            onClick={handleCancel}
            disabled={isCapturing}
          >
            ✕
          </button>
        </div>
      )}
    </Column>
  );
};

export default SimpleCamera;
